(function () {
  'use strict';

  var MEASUREMENT_ID = 'G-DVDMH7GJZ6';
  var DEFAULT_DEDUPE_MS = 2000;
  var FAST_DEDUPE_MS = 450;
  var CHANGE_DELAY_MS = 90;
  var READY_POLL_MS = 150;
  var READY_TIMEOUT_MS = 10000;
  var MAX_QUEUE = 50;
  var MAX_LOG = 100;

  var win = window;
  var doc = document;
  var omp = win.omp = win.omp || {};
  var debug = /(?:^|[?&])ga4_debug=1(?:&|$)/.test(win.location.search);
  var queue = [];
  var dedupe = new Map();
  var logBuffer = [];
  var readyStartedAt = Date.now();
  var readyTimer = null;
  var deferredControlTimers = new WeakMap();

  function debugLog(type, payload) {
    var entry = {
      type: type,
      payload: payload || {},
      at: new Date().toISOString()
    };
    logBuffer.push(entry);
    if (logBuffer.length > MAX_LOG) logBuffer.shift();
    if (debug && win.console && typeof win.console.log === 'function') {
      win.console.log('[OMP GA4]', type, payload || {});
    }
  }

  function consentAllowed() {
    try {
      if (omp.consentGate && typeof omp.consentGate.isAllowed === 'function') {
        return !!omp.consentGate.isAllowed('analytics');
      }
      if (win.Shopify && win.Shopify.customerPrivacy) {
        var cp = win.Shopify.customerPrivacy;
        if (typeof cp.analyticsProcessingAllowed === 'function') {
          return !!cp.analyticsProcessingAllowed();
        }
        if (typeof cp.currentVisitorConsent === 'function') {
          var current = cp.currentVisitorConsent() || {};
          if (Object.prototype.hasOwnProperty.call(current, 'analytics')) {
            return current.analytics === 'yes' || current.analytics === true;
          }
        }
      }
    } catch (err) {
      debugLog('consent_check_error', { message: err && err.message });
    }
    return true;
  }

  function isReady() {
    return typeof win.gtag === 'function' && Array.isArray(win.dataLayer);
  }

  function toSnake(value) {
    return String(value || '')
      .trim()
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .replace(/[^a-zA-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase();
  }

  function sanitizeEventName(name) {
    var clean = toSnake(name).slice(0, 40);
    return clean || 'element_click';
  }

  function isDeniedKey(key) {
    var clean = toSnake(key);
    return /(^|_)(email|phone|address|first_name|last_name|full_name|customer_id|customer_token|token|password|authorization|auth|secret|api_key)($|_)/.test(clean);
  }

  function isHashableOperationalId(key) {
    var clean = toSnake(key);
    return clean === 'subscription_id' ||
      clean === 'recharge_subscription_id' ||
      clean === 'purchase_item_id' ||
      clean === 'recharge_purchase_item_id';
  }

  function hashKeyFor(key) {
    var clean = toSnake(key);
    if (clean.indexOf('purchase_item') !== -1) return 'custom_purchase_item_hash';
    return 'custom_subscription_hash';
  }

  function looksLikePii(value) {
    if (typeof value !== 'string') return false;
    return /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value) ||
      /(?:\+?1[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/.test(value);
  }

  function trimString(value, max) {
    return String(value).replace(/\s+/g, ' ').trim().slice(0, max || 100);
  }

  function sanitizeValue(value, key) {
    if (value === null || typeof value === 'undefined') return undefined;
    if (typeof value === 'boolean' || typeof value === 'number') return value;
    if (typeof value === 'string') {
      if (looksLikePii(value)) return undefined;
      var max = toSnake(key).indexOf('location') !== -1 ? 500 : 120;
      return trimString(value, max);
    }
    if (Array.isArray(value)) {
      var arrayValue = value.map(function (item) {
        return sanitizeValue(item, key);
      }).filter(function (item) {
        return typeof item !== 'undefined';
      });
      return trimString(JSON.stringify(arrayValue), 500);
    }
    if (typeof value === 'object') {
      try {
        return trimString(JSON.stringify(value), 500);
      } catch (err) {
        return undefined;
      }
    }
    return undefined;
  }

  function sha256(value) {
    var input = String(value || '').trim();
    if (!input) return Promise.resolve(null);
    if (!win.crypto || !win.crypto.subtle || !win.TextEncoder) {
      return Promise.resolve(null);
    }
    return win.crypto.subtle.digest('SHA-256', new win.TextEncoder().encode(input))
      .then(function (buffer) {
        return Array.prototype.map.call(new Uint8Array(buffer), function (byte) {
          return ('00' + byte.toString(16)).slice(-2);
        }).join('');
      })
      .catch(function () {
        return null;
      });
  }

  function baseParams() {
    var bodyClass = doc.body ? doc.body.className || '' : '';
    var templateMatch = String(bodyClass).match(/template-([^\s]+)/);
    return {
      page_path: win.location.pathname,
      page_title: doc.title,
      page_location: win.location.href,
      template: templateMatch ? templateMatch[1] : '',
      event_source: 'theme'
    };
  }

  function stableStringify(value) {
    if (!value || typeof value !== 'object') return JSON.stringify(value);
    if (Array.isArray(value)) {
      return '[' + value.map(stableStringify).join(',') + ']';
    }
    return '{' + Object.keys(value).sort().map(function (key) {
      return JSON.stringify(key) + ':' + stableStringify(value[key]);
    }).join(',') + '}';
  }

  function elementIdentity(el) {
    if (!el) return '';
    var dataId = el.getAttribute('data-click-id') || el.getAttribute('data-ga4-id');
    if (dataId) return 'data:' + dataId;
    if (el.id) return 'id:' + el.id;
    if (el.name) return 'name:' + el.name;
    if (el.href) return 'href:' + el.href;
    var label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
    if (!label && el.textContent) label = el.textContent;
    return toSnake(el.tagName + '_' + trimString(label, 60));
  }

  function dedupeWindow(eventName, params, options) {
    if (options && typeof options.dedupeMs === 'number') return options.dedupeMs;
    var event = sanitizeEventName(eventName);
    if (/quantity|qty|slider|carousel|slide/.test(event)) return FAST_DEDUPE_MS;
    if (params && (params.custom_quantity_delta || params.custom_selected_value || params.custom_variant_id || params.custom_product_id)) {
      return FAST_DEDUPE_MS;
    }
    return DEFAULT_DEDUPE_MS;
  }

  function shouldDedupe(eventName, params, options) {
    if (options && options.skipDedupe) return false;
    var windowMs = dedupeWindow(eventName, params, options || {});
    if (!windowMs) return false;
    var key = [
      sanitizeEventName(eventName),
      win.location.pathname,
      options && (options.dedupeKey || elementIdentity(options.element)),
      stableStringify(params || {})
    ].join('|');
    var now = Date.now();
    var previous = dedupe.get(key);
    dedupe.set(key, now);
    dedupe.forEach(function (timestamp, storedKey) {
      if (now - timestamp > DEFAULT_DEDUPE_MS * 3) dedupe.delete(storedKey);
    });
    return previous && now - previous < windowMs;
  }

  function normalizeParams(params) {
    var input = Object.assign({}, baseParams(), params || {});
    var output = {};
    var hashJobs = [];

    Object.keys(input).forEach(function (key) {
      var normalizedKey = toSnake(key).slice(0, 40);
      if (!normalizedKey) return;

      if (isHashableOperationalId(normalizedKey)) {
        hashJobs.push(sha256(input[key]).then(function (hash) {
          if (hash) output[hashKeyFor(normalizedKey)] = hash;
        }));
        return;
      }

      if (isDeniedKey(normalizedKey)) return;

      var value = sanitizeValue(input[key], normalizedKey);
      if (typeof value !== 'undefined' && value !== '') {
        output[normalizedKey] = value;
      }
    });

    output.send_to = MEASUREMENT_ID;
    output.transport_type = 'beacon';
    return Promise.all(hashJobs).then(function () {
      return output;
    });
  }

  function sendNow(eventName, params) {
    if (!consentAllowed()) {
      debugLog('dropped_consent_denied', { event: eventName });
      return;
    }
    if (!isReady()) {
      enqueue(eventName, params);
      return;
    }
    normalizeParams(params).then(function (normalized) {
      if (!consentAllowed()) {
        debugLog('dropped_consent_denied', { event: eventName });
        return;
      }
      try {
        win.gtag('event', sanitizeEventName(eventName), normalized);
        debugLog('sent', { event: sanitizeEventName(eventName), params: normalized });
      } catch (err) {
        debugLog('send_error', { event: eventName, message: err && err.message });
      }
    });
  }

  function enqueue(eventName, params) {
    if (!consentAllowed()) {
      debugLog('dropped_consent_denied', { event: eventName });
      return;
    }
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push({ eventName: eventName, params: params || {}, at: Date.now() });
    debugLog('queued', { event: eventName, queue_length: queue.length });
    startReadyPolling();
  }

  function flushQueue() {
    if (!isReady()) return;
    var pending = queue.splice(0, queue.length);
    pending.forEach(function (item) {
      if (Date.now() - item.at <= READY_TIMEOUT_MS) {
        sendNow(item.eventName, item.params);
      } else {
        debugLog('dropped_queue_timeout', { event: item.eventName });
      }
    });
  }

  function startReadyPolling() {
    if (readyTimer) return;
    readyStartedAt = Date.now();
    readyTimer = win.setInterval(function () {
      if (isReady()) {
        win.clearInterval(readyTimer);
        readyTimer = null;
        flushQueue();
      } else if (Date.now() - readyStartedAt > READY_TIMEOUT_MS) {
        win.clearInterval(readyTimer);
        readyTimer = null;
        queue = [];
        debugLog('dropped_ga4_not_ready', {});
      }
    }, READY_POLL_MS);
  }

  function track(eventName, params, options) {
    var event = sanitizeEventName(eventName);
    var eventParams = Object.assign({}, params || {});
    var eventOptions = options || {};
    if (!consentAllowed()) {
      debugLog('dropped_consent_denied', { event: event });
      return false;
    }
    if (shouldDedupe(event, eventParams, eventOptions)) {
      debugLog('deduped', { event: event });
      return false;
    }
    if (isReady()) {
      sendNow(event, eventParams);
    } else {
      enqueue(event, eventParams);
    }
    return true;
  }

  function closestWithData(el, attr) {
    var current = el;
    while (current && current !== doc.documentElement) {
      if (current.getAttribute && current.getAttribute(attr)) return current;
      current = current.parentElement;
    }
    return null;
  }

  function readDataParams(el) {
    var params = {};
    if (!el || !el.dataset) return params;
    Object.keys(el.dataset).forEach(function (key) {
      if (key.indexOf('ga4Param') !== 0) return;
      var paramName = toSnake(key.replace(/^ga4Param/, ''));
      params[paramName] = el.dataset[key];
    });
    return params;
  }

  function readInheritedDataParams(el) {
    var params = {};
    var stack = [];
    var current = el;
    while (current && current !== doc.documentElement) {
      stack.unshift(current);
      current = current.parentElement;
    }
    stack.forEach(function (node) {
      Object.assign(params, readDataParams(node));
    });
    return params;
  }

  function dataContext(el) {
    var params = {};
    var keys = [
      ['data-click-id', 'click_id'],
      ['data-click-link-name', 'click_link_name'],
      ['data-click-context', 'click_context'],
      ['data-click-flow', 'flow'],
      ['data-click-section', 'section_id'],
      ['data-click-product', 'custom_item_name'],
      ['data-click-productid', 'custom_product_id'],
      ['data-click-variant', 'custom_variant_name'],
      ['data-click-variantid', 'custom_variant_id'],
      ['data-click-collection', 'custom_collection']
    ];

    keys.forEach(function (pair) {
      var contextEl = closestWithData(el, pair[0]);
      if (contextEl) params[pair[1]] = contextEl.getAttribute(pair[0]);
    });

    return Object.assign(params, readInheritedDataParams(el));
  }

  function inferClickName(el) {
    return el.getAttribute('data-click-name') ||
      el.getAttribute('aria-label') ||
      el.getAttribute('title') ||
      trimString(el.textContent || el.value || el.name || el.tagName, 80);
  }

  function controlValue(el) {
    if (!el) return {};
    var role = el.getAttribute('role');
    var params = dataContext(el);
    params.custom_selected_value = el.value || el.getAttribute('aria-label') || trimString(el.textContent || '', 80);
    params.custom_control_type = role || (el.type || el.tagName || '').toLowerCase();
    if (typeof el.checked !== 'undefined') params.custom_checked = !!el.checked;
    if (el.getAttribute('aria-checked') !== null) params.custom_checked = el.getAttribute('aria-checked') === 'true';
    if (el.tagName === 'SELECT') {
      var option = el.options[el.selectedIndex];
      params.custom_selected_label = option ? option.text : '';
      params.custom_selected_index = el.selectedIndex;
    }
    return params;
  }

  function scheduleControlTrack(el, eventName) {
    var previous = deferredControlTimers.get(el);
    if (previous) win.clearTimeout(previous);
    var timer = win.setTimeout(function () {
      win.requestAnimationFrame(function () {
        track(eventName, controlValue(el), {
          element: el,
          dedupeMs: FAST_DEDUPE_MS
        });
      });
    }, CHANGE_DELAY_MS);
    deferredControlTimers.set(el, timer);
  }

  function isFormControl(el) {
    if (!el) return false;
    return /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName);
  }

  function findInteractive(target) {
    if (!target || !target.closest) return null;
    return target.closest('[data-ga4-event], [data-click-name], a, button, input, select, textarea, [role="button"], [role="link"], [role="radio"], [role="switch"], [role="checkbox"], summary');
  }

  function onClick(event) {
    var el = findInteractive(event.target);
    if (!el) return;

    var role = el.getAttribute('role');
    if (role === 'radio' || role === 'switch' || role === 'checkbox') {
      scheduleControlTrack(el, el.getAttribute('data-ga4-event') || 'option_selected');
      return;
    }

    if (isFormControl(el) && el.type !== 'submit' && el.type !== 'button' && !el.hasAttribute('data-click-name') && !el.hasAttribute('data-ga4-event')) {
      return;
    }

    var eventName = el.getAttribute('data-ga4-event') || 'element_click';
    var params = dataContext(el);
    params.click_name = inferClickName(el);
    params.click_tag = (el.tagName || '').toLowerCase();
    if (el.href) params.click_url = el.href;

    track(eventName, params, {
      element: el,
      dedupeMs: DEFAULT_DEDUPE_MS
    });
  }

  function onChange(event) {
    var el = event.target;
    if (!isFormControl(el)) return;
    var eventName = el.getAttribute('data-ga4-event');
    if (!eventName) eventName = el.tagName === 'SELECT' ? 'select_change' : 'option_selected';
    scheduleControlTrack(el, eventName);
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    var params = dataContext(form);
    params.form_id = form.id || form.getAttribute('data-form-id') || '';
    params.form_action = form.getAttribute('action') || '';
    params.form_method = (form.getAttribute('method') || 'get').toLowerCase();
    if (params.form_action.indexOf('/search') !== -1 || form.getAttribute('role') === 'search') {
      var searchInput = form.querySelector('input[name="q"]');
      var searchTerm = searchInput ? trimString(searchInput.value || '', 80) : '';
      params.custom_search_term = searchTerm;
      params.custom_search_length = searchTerm.length;
    }
    track(form.getAttribute('data-ga4-event') || 'form_submit', params, {
      element: form,
      dedupeMs: DEFAULT_DEDUPE_MS
    });
  }

  function onCustomEvent(event) {
    var detail = event.detail || {};
    track(detail.event || detail.eventName || detail.name, detail.params || detail.parameters || {}, detail.options || {});
  }

  function bindSlickSliders() {
    if (!win.jQuery) return;
    win.jQuery('.slick-initialized, .js-slider-collection, .js_superfood-slider, .js_testimonial-slider, .js-slider-primary, .js-slider-thumbs').each(function () {
      var slider = win.jQuery(this);
      if (slider.data('ompGa4Bound')) return;
      slider.data('ompGa4Bound', true);
      slider.on('afterChange', function (event, slick, currentSlide) {
        var el = event.currentTarget;
        track('slider_change', Object.assign(dataContext(el), {
          custom_selected_index: currentSlide,
          custom_item_count: slick && slick.slideCount,
          click_context: el.className || ''
        }), {
          element: el,
          dedupeMs: FAST_DEDUPE_MS
        });
      });
    });
  }

  function consumePreQueue() {
    var preQueue = win.ompGa4Queue || [];
    if (!Array.isArray(preQueue)) return;
    preQueue.splice(0, preQueue.length).forEach(function (item) {
      if (!item) return;
      track(item.eventName || item.event || item.name, item.params || {}, item.options || {});
    });
  }

  function attach() {
    doc.addEventListener('click', onClick, true);
    doc.addEventListener('change', onChange, true);
    doc.addEventListener('submit', onSubmit, true);
    doc.addEventListener('omp:ga4', onCustomEvent, false);
    doc.addEventListener('visitorConsentCollected', flushQueue, false);
    consumePreQueue();
    bindSlickSliders();
    win.setTimeout(bindSlickSliders, 1000);
    win.setTimeout(bindSlickSliders, 3000);
    startReadyPolling();
  }

  omp.ga4 = {
    track: track,
    isReady: isReady,
    consentAllowed: consentAllowed,
    flush: flushQueue,
    getDebugEvents: function () {
      return logBuffer.slice();
    }
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', attach, { once: true });
  } else {
    attach();
  }
})();
